// トップページ Links タブ (LinkBoard) のテキスト形式パーサ。
// 1 件 = 最大 2 行 (1 行目が URL の行、次の行が解説) のブックマーク記法を扱う。

const MD_LINK = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/;
const URL_IN_LINE = /https?:\/\/\S+/;

export type LinkRow = {
  kind: 'link';
  label: string;
  url: string;
  /** URL の次の行に書かれた解説 (複数行なら改行で連結)。 */
  desc: string;
  /** ラベルを明示指定されたか (auto ラベルのときは右肩のホスト表示が重複するので出さない)。 */
  explicitLabel: boolean;
};
export type HeadingRow = { kind: 'heading'; label: string };
export type Row = HeadingRow | LinkRow;

/** 表示用のホスト名 (www 除去)。URL として解釈できなければ元の文字列を返す。 */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** 解説が無いときに 2 行目に出す控えめな要約: パスのみ (トラッキングクエリは捨てる)。 */
export function pathSummary(url: string): string {
  try {
    const u = new URL(url);
    const path = decodeURIComponent(u.pathname).replace(/\/$/, '');
    return (path || '/').slice(0, 120);
  } catch {
    return url;
  }
}

/**
 * ブックマーク形式のテキストを解釈する。
 * - 「# 見出し」          → セクション区切り
 * - 「[ラベル](URL)」     → ラベル付きリンク
 * - 「ラベル URL」/「URL」 → 行内の最初の URL を拾い、手前にテキストがあればラベルにする
 * - URL を含まない行      → 直前のリンクの解説 (2 行目)。連続すれば改行で連結する
 * 直前にリンクが無い状態の解説行は捨てる (貼り付けたゴミ行がそのまま出ないように)。
 */
export function parseLinks(raw: string): Row[] {
  const out: Row[] = [];
  let last: LinkRow | null = null;
  for (const line of raw.split('\n')) {
    const s = line.trim();
    if (!s) continue;
    if (s.startsWith('#')) {
      out.push({ kind: 'heading', label: s.replace(/^#+\s*/, '') });
      last = null;
      continue;
    }
    const md = s.match(MD_LINK);
    if (md) {
      last = { kind: 'link', label: md[1].trim(), url: md[2], desc: '', explicitLabel: true };
      out.push(last);
      continue;
    }
    const m = s.match(URL_IN_LINE);
    if (!m) {
      if (last) last.desc = last.desc ? `${last.desc}\n${s}` : s;
      continue;
    }
    const url = m[0];
    const label = s.slice(0, m.index).trim();
    last = { kind: 'link', label: label || hostOf(url), url, desc: '', explicitLabel: Boolean(label) };
    out.push(last);
  }
  return out;
}
